import React, { useState, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';

import { makeStyles } from '@material-ui/core/styles';
import SpeedDial from '@material-ui/lab/SpeedDial';
import SpeedDialAction from '@material-ui/lab/SpeedDialAction';

import HelpOutlineOutlinedIcon from '@material-ui/icons/HelpOutlineOutlined';
import EmailIcon from '@material-ui/icons/Email';
import CallIcon from '@material-ui/icons/Call';
import ChatIcon from '@material-ui/icons/Chat';
import AssignmentOutlinedIcon from '@material-ui/icons/AssignmentOutlined';
import SettingsBackupRestoreIcon from '@material-ui/icons/SettingsBackupRestore';

import { track } from 'core/helpers/analytics';
import useSession from 'core/hooks/use-session';
import {
  useFloatingPosition,
  useHasMovedFloatingButtons,
  resetFloatingPositions
} from 'core/hooks/use-floating-position';
import { useTheme } from '@material-ui/core/styles';
import { useLocation } from 'react-router-dom';

import { isBranchEnv, appConfig, brandConfig } from 'brand-config';
import { openFrontChatPopup } from 'core/components/front-chat';
import AgencyHelpModal from './agency-help-modal/agency-help-modal';

// MUI's default (large) Fab is 56px square.
const FAB_SIZE = 56;

const useStyles = makeStyles((theme) => ({
  floatingContainer: ({ quotePage, position }) => ({
    position: quotePage ? 'absolute' : 'fixed',
    bottom: quotePage ? undefined : (position?.bottom ?? 200),
    top: quotePage ? '-208px' : undefined,
    right: position?.right ?? 35,
    width: quotePage ? undefined : FAB_SIZE,
    height: quotePage ? undefined : FAB_SIZE,
    zIndex: 200
  }),
  // Touch panning or text selection would otherwise cancel or muddy a drag.
  dragHandle: { touchAction: 'none', userSelect: 'none' },
  // The fixed wrapper is only as big as the button, so the menu can open either way without moving it.
  speedDial: ({ quotePage, direction }) =>
    quotePage ? {} : { position: 'absolute', right: 0, [direction === 'down' ? 'top' : 'bottom']: 0 },
  tooltip: {
    backgroundColor: theme.colorPalette.green_75
  },
  actionIcon: {
    color: theme.colorPalette.green_75
  }
}));

const StaffSpeedDial = ({ quotePage = false }) => {
  const theme = useTheme();
  const location = useLocation();
  const rootRef = useRef(null);
  const { position, startDrag } = useFloatingPosition('speedDial');
  const hasMovedFloatingButtons = useHasMovedFloatingButtons();
  // The quote page renders its own copy inside the quote footer, which stays put.
  const fixedPosition = quotePage ? null : position;
  // Moved into the top half of the screen, the menu opens downward so it stays on screen.
  const direction =
    fixedPosition && fixedPosition.bottom + fixedPosition.height / 2 > document.documentElement.clientHeight / 2
      ? 'down'
      : 'up';
  const classes = useStyles({ quotePage, position: fixedPosition, direction });
  const [open, setOpen] = useState(false);
  const history = useHistory();
  const session = useSession();
  const [openAgencyHelpModal, setOpenAgencyHelpModal] = useState(false);
  const URL = `https://${window.location.host}${history.location.pathname}`;
  const affinity = session?.user?.affinityCode;

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleClick = () => {
    // keeps the chat from closing if user clicks on it
    setOpen(!!open);
  };

  const openChat = () => {
    track('Open agency support chat', {
      URL,
      affinity
    });

    openFrontChatPopup();
  };

  // Branch's agent help-request form is a Branch Typeform; an Ember Tech mutual brand has none, and
  // gets no "Submit a help request" action rather than Branch's form.
  const helpRequestFormUrl = brandConfig?.typeformUrls?.agencySupport;

  const handleBugReport = () => {
    track('Contact Agency support via typeform', {
      URL,
      affinity
    });

    window.open(helpRequestFormUrl, '_blank', 'noopener,noreferrer');
  };

  const openFnolForm = (fnolUrl) => {
    track('Open FNOL form', {
      URL,
      affinity
    });

    window.open(fnolUrl, '_blank', 'noopener,noreferrer');
  };

  const showAgencyModal = () => {
    track('Contact Agency support via phone', {
      URL,
      affinity
    });

    setOpenAgencyHelpModal(true);
  };

  const shouldDisplay = useMemo(() => {
    return location.pathname === '/quote' && !quotePage;
  }, [location.pathname, quotePage]);

  const helpWidget = appConfig.helpWidget;
  if ((!isBranchEnv && !helpWidget) || shouldDisplay) {
    return null;
  }

  const chatLabel = session.isInternalAgent ? 'Internal Help Desk' : 'Open Agency Support Chat';

  return (
    <div className={classes.floatingContainer} ref={rootRef}>
      {openAgencyHelpModal && (
        <AgencyHelpModal setOpenAgencyHelpModal={setOpenAgencyHelpModal} username={session?.user?.username || ''} />
      )}
      <SpeedDial
        open={open}
        onClick={handleClick}
        onClose={handleClose}
        onOpen={handleOpen}
        icon={<HelpOutlineOutlinedIcon />}
        ariaLabel="Staff Speed Dial"
        className={classes.speedDial}
        direction={direction}
        FabProps={
          quotePage
            ? undefined
            : { className: classes.dragHandle, onPointerDown: (event) => startDrag(event, rootRef.current) }
        }
      >
        {open &&
          !isBranchEnv &&
          helpWidget?.fnolLinks?.map(({ label, url }) => (
            <SpeedDialAction
              key={label}
              icon={<AssignmentOutlinedIcon style={{ color: theme.colorPalette.green_75 }} />}
              tooltipTitle={label}
              TooltipClasses={{ tooltip: classes.tooltip }}
              onClick={() => openFnolForm(url)}
            />
          ))}
        {open && helpRequestFormUrl && (isBranchEnv ? !session.isInternalAgent : helpWidget?.showEmail) && (
          <SpeedDialAction
            key="Submit a help request"
            icon={<EmailIcon style={{ color: theme.colorPalette.green_75 }} />}
            tooltipTitle="Submit a help request"
            TooltipClasses={{ tooltip: classes.tooltip }}
            onClick={handleBugReport}
          />
        )}
        {open && (isBranchEnv ? !session.isInternalAgent : helpWidget?.showPhone) && (
          <SpeedDialAction
            key="Show agency support contact number"
            icon={<CallIcon style={{ color: theme.colorPalette.green_75 }} />}
            TooltipClasses={{ tooltip: classes.tooltip }}
            tooltipTitle="Show agency support contact number"
            onClick={showAgencyModal}
          />
        )}
        {open && (isBranchEnv || helpWidget?.showChat) && (
          <SpeedDialAction
            key={chatLabel}
            icon={<ChatIcon style={{ color: theme.colorPalette.green_75 }} />}
            TooltipClasses={classes}
            tooltipTitle={chatLabel}
            onClick={openChat}
          />
        )}
        {open && hasMovedFloatingButtons && (
          <SpeedDialAction
            key="Move buttons back"
            icon={<SettingsBackupRestoreIcon className={classes.actionIcon} />}
            TooltipClasses={{ tooltip: classes.tooltip }}
            tooltipTitle="Move buttons back"
            onClick={resetFloatingPositions}
          />
        )}
      </SpeedDial>
    </div>
  );
};

StaffSpeedDial.propTypes = {
  quotePage: PropTypes.bool
};

export default StaffSpeedDial;
